import { queriesRequirementDefinitions } from "@query";
import { WSModalCustomProps } from "@wingspanhq/fe-component-library";
import { WSQueryCache } from "@ws-react-query";
import React, { useMemo } from "react";

import { ModalFlow } from "../../../../shared/components/ModalFlow/ModalFlow";
import { ModalFlowState } from "../../../../shared/components/ModalFlow/types";
import { useModalFlow } from "../../../../shared/components/ModalFlow/useModalFlow";

import { StepConfigureSettings } from "./StepConfigureSettings";
import { StepReviewAndCreate } from "./StepReviewAndCreate";
import { StepSetTransition } from "./StepSetTransition";
import { DEFAULT_TRANSITION_DATA } from "./StepSetTransition/utils";
import { SuccessStep } from "./SuccessStep";
import { FlowCreateNewVersionState } from "./types";
import { useModalConfirmExit } from "./useModalConfirmExit";

const FLOW_STORAGE_KEY = "FLOW_CREATE_NEW_VERSION";

const getInitialState = (
  requirementDefinitionId: string
): ModalFlowState<FlowCreateNewVersionState> => ({
  state: {
    requirementDefinitionId,
    transitionData: DEFAULT_TRANSITION_DATA
  }
});

type FlowCreateNewVersionProps = {
  requirementDefinitionId: string;
  onFinish: (status: "success" | "cancel") => void;
} & WSModalCustomProps<void>;

const FlowCreateNewVersion: React.FC<FlowCreateNewVersionProps> = ({
  onFinish,
  onClose,
  requirementDefinitionId
}) => {
  const confirmExitModal = useModalConfirmExit();

  const flowInitialState = useMemo(
    () => getInitialState(requirementDefinitionId),
    [requirementDefinitionId]
  );

  return (
    <ModalFlow<FlowCreateNewVersionState>
      confirmExitModal={confirmExitModal}
      defaultStatuses
      historyStateKey={FLOW_STORAGE_KEY}
      initialState={flowInitialState}
      sideSteps={[
        {
          component: SuccessStep,
          key: "success",
          title: "Success"
        }
      ]}
      steps={[
        {
          key: "requirement-settings",
          steps: [
            {
              component: StepConfigureSettings,
              key: "configure-settings",
              title: "Configure settings"
            },
            {
              component: StepSetTransition,
              confirmExit: true,
              key: "set-transition",
              title: "Set transition"
            },
            {
              component: StepReviewAndCreate,
              key: "review-and-create",
              title: "Review & create"
            }
          ],
          title: "Requirement settings"
        }
      ]}
      title="Create new version"
      onClose={onClose}
      onFinish={onFinish}
    />
  );
};

export const useFlowCreateNewVersion = () =>
  useModalFlow({
    Component: FlowCreateNewVersion,
    flowName: "create-new-version",
    getProps({ requirementDefinitionId }) {
      return requirementDefinitionId
        ? {
            requirementDefinitionId
          }
        : undefined;
    },
    onFinish(status) {
      if (status === "success") {
        WSQueryCache.removeQueries(
          queriesRequirementDefinitions.getRequirementDefinition.queryKeyBase
        );
      }
    }
  });
